import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminLogin,
  adminCreateItem,
  adminUpdateItem,
  adminDeleteItem,
  adminQuotes,
  adminDeleteQuote,
  adminCreateCategory,
  adminUpdateCategory,
  adminDeleteCategory,
  adminReorderCategories,
  adminCatalog,
  adminSetZoneImage,
  adminDeleteZoneImage,
  adminAiRules,
  adminCreateAiRule,
  adminUpdateAiRule,
  adminDeleteAiRule,
  uploadZoneImage,
  getZoneImages,
  uploadItemImage,
  mediaUrl,
  fmt,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BASIS_OPTIONS } from "../lib/surfaces";
import { AdminMonitor } from "../components/AdminMonitor";
import { AdminDataIO } from "../components/AdminDataIO";
import { ArrowLeft, Lock, Plus, Save, Trash2, Upload, ImageIcon, ChevronUp, ChevronDown, FileText } from "lucide-react";

const TOKEN_KEY = "rf_admin_token";

const RELATIONS = ["consigliata", "da_verificare", "alternativa"];

const relatedToText = (related) =>
  (related || []).map((r) => `${r.name} | ${r.relation || "consigliata"}`).join("\n");

const parseRelated = (text) =>
  (text || "")
    .split("\n")
    .map((line) => line.split("|").map((p) => p.trim()))
    .filter(([name]) => name)
    .map(([name, relation]) => ({
      name,
      relation: RELATIONS.includes(relation) ? relation : "consigliata",
    }));

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [pwd, setPwd] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [openQuote, setOpenQuote] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [openItem, setOpenItem] = useState(null);
  const [zoneImages, setZoneImages] = useState({});
  const [newZoneType, setNewZoneType] = useState("");
  const [aiRules, setAiRules] = useState([]);
  const [newAiRule, setNewAiRule] = useState("");

  const zoneTypes = React.useMemo(() => {
    const base = [...(catalog?.internal_types || []), ...(catalog?.external_types || [])];
    return [...new Set([...base, ...Object.keys(zoneImages)])];
  }, [catalog, zoneImages]);

  const uploadZonePhoto = async (type, file) => {
    if (!file) return;
    try {
      const { image_url } = await uploadZoneImage(token, type, file);
      setZoneImages((z) => ({ ...z, [type]: image_url }));
      toast.success(`Foto ${type} aggiornata`);
    } catch {
      toast.error("Upload immagine non riuscito");
    }
  };

  const saveZoneUrl = async (type, url) => {
    try {
      await adminSetZoneImage(token, type, url);
      setZoneImages((z) => ({ ...z, [type]: url }));
      toast.success(url ? `Foto ${type} salvata` : `Foto ${type} rimossa`);
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const removeZonePhoto = async (type) => {
    try {
      await adminDeleteZoneImage(token, type);
      setZoneImages((z) => {
        const next = { ...z };
        delete next[type];
        return next;
      });
      toast.success("Foto rimossa");
    } catch {
      toast.error("Rimozione non riuscita");
    }
  };

  const moveCategory = async (key, dir) => {
    const cats = [...catalog.categories];
    const i = cats.findIndex((c) => c.key === key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cats.length) return;
    [cats[i], cats[j]] = [cats[j], cats[i]];
    setCatalog((c) => ({ ...c, categories: cats }));
    try {
      await adminReorderCategories(token, cats.map((c) => c.key));
      setCatalog(await adminCatalog(token));
      toast.success("Ordine categorie aggiornato");
    } catch {
      toast.error("Riordino non riuscito");
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) {
      toast.error("Inserisci il nome della categoria");
      return;
    }
    try {
      await adminCreateCategory(token, newCategory);
      setNewCategory("");
      setCatalog(await adminCatalog(token));
      toast.success("Categoria aggiunta");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Creazione non riuscita");
    }
  };

  const saveCategory = async (cat) => {
    try {
      await adminUpdateCategory(token, cat.key, { name: cat.name });
      toast.success("Categoria aggiornata");
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const removeCategory = async (key) => {
    try {
      await adminDeleteCategory(token, key);
      setCatalog(await adminCatalog(token));
      toast.success("Categoria eliminata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };
  const [draft, setDraft] = useState({ category: "demolizioni", name: "", subcategory: "", unit: "m2", price: "", default_qty: 1, surface_basis: "floor" });

  const load = async (t) => {
    try {
      const c = await adminCatalog(t);
      setCatalog(c);
    } catch {
      toast.error("Impossibile caricare il listino");
    }
    try {
      setQuotes(await adminQuotes(t));
    } catch {
      setQuotes([]);
    }
    try {
      setZoneImages(await getZoneImages(true));
    } catch {
      setZoneImages({});
    }
    try {
      setAiRules(await adminAiRules(t));
    } catch {
      setAiRules([]);
    }
  };

  const addAiRule = async () => {
    const text = newAiRule.trim();
    if (!text) return toast.error("Inserisci il testo della regola");
    try {
      const rule = await adminCreateAiRule(token, text);
      setAiRules((r) => [...r, rule]);
      setNewAiRule("");
      toast.success("Regola aggiunta");
    } catch {
      toast.error("Creazione regola non riuscita");
    }
  };

  const saveAiRule = async (id, text) => {
    if (!text.trim()) return toast.error("Il testo della regola non può essere vuoto");
    try {
      await adminUpdateAiRule(token, id, text.trim());
      setAiRules((r) => r.map((x) => (x.id === id ? { ...x, text: text.trim() } : x)));
      toast.success("Regola aggiornata");
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const removeAiRule = async (id) => {
    try {
      await adminDeleteAiRule(token, id);
      setAiRules((r) => r.filter((x) => x.id !== id));
      toast.success("Regola eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  useEffect(() => {
    if (!token) return;
    adminLogin(token)
      .then(() => {
        setAuthed(true);
        load(token);
      })
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      });
  }, [token]);

  const doLogin = async (e) => {
    e.preventDefault();
    try {
      await adminLogin(pwd);
      localStorage.setItem(TOKEN_KEY, pwd);
      setToken(pwd);
    } catch {
      toast.error("Password non valida");
    }
  };

  const editField = (id, field, value) =>
    setCatalog((c) => ({
      ...c,
      items: c.items.map((i) => (i.id === id ? { ...i, [field]: value } : i)),
    }));

  const toggleActive = async (item) => {
    const active = item.active === false;
    editField(item.id, "active", active);
    try {
      await adminUpdateItem(token, item.id, { active });
      toast.success(active ? `${item.name}: attivo` : `${item.name}: spento`);
    } catch {
      editField(item.id, "active", !active);
      toast.error("Aggiornamento non riuscito");
    }
  };

  const save = async (item) => {
    try {
      await adminUpdateItem(token, item.id, {
        name: item.name,
        price: parseFloat(item.price) || 0,
        unit: item.unit,
        default_qty: parseFloat(item.default_qty) || 1,
        image_url: item.image_url || "",
        category: item.category,
        subcategory: item.subcategory || "",
        description: item.description || "",
        included: item.included || "",
        excluded: item.excluded || "",
        ai_note: item.ai_note || "",
        related: parseRelated(item.related_text ?? relatedToText(item.related)),
        surface_basis: item.surface_basis || "none",
        visible: item.visible !== false,
        active: item.active !== false,
      });
      setCatalog(await adminCatalog(token));
      toast.success("Lavorazione aggiornata");
    } catch {
      toast.error("Salvataggio non riuscito");
    }
  };

  const uploadImage = async (itemId, file) => {
    if (!file) return;
    try {
      const { image_url } = await uploadItemImage(token, itemId, file);
      editField(itemId, "image_url", image_url);
      toast.success("Immagine caricata");
    } catch {
      toast.error("Upload immagine non riuscito");
    }
  };

  const remove = async (id) => {
    try {
      await adminDeleteItem(token, id);
      setCatalog((c) => ({ ...c, items: c.items.filter((i) => i.id !== id) }));
      toast.success("Lavorazione eliminata");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const add = async () => {
    if (!draft.name.trim() || !draft.price) {
      toast.error("Nome e prezzo obbligatori");
      return;
    }
    try {
      const created = await adminCreateItem(token, {
        ...draft,
        price: parseFloat(draft.price),
        default_qty: parseFloat(draft.default_qty) || 1,
      });
      const fresh = await adminCatalog(token);
      setCatalog(fresh);
      setDraft({ ...draft, name: "", price: "" });
      toast.success(`"${created.name}" aggiunta in ${
        fresh.categories.find((c) => c.key === created.category)?.name || created.category
      }`);
    } catch {
      toast.error("Creazione non riuscita");
    }
  };

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0E1A13] px-5">
        <form
          onSubmit={doLogin}
          className="w-full max-w-sm border border-[#1F3A29] bg-[#12241A] p-8"
          data-testid="admin-login-form"
        >
          <Lock className="h-6 w-6 text-[#4E7C59]" />
          <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-tight text-[#F4F7F2]">
            BioEdil Abruzzo
          </h1>
          <p className="label-xs mt-2 text-[#9BB0A2]">Area riservata · gestione listino</p>
          <Input
            data-testid="admin-password-input"
            type="password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            placeholder="Password"
            className="mt-6 rounded-none border-[#1F3A29] bg-[#0E1A13] text-[#F4F7F2]"
          />
          <Button
            data-testid="admin-login-btn"
            type="submit"
            className="mt-3 w-full rounded-none bg-[#4E7C59] text-white hover:bg-[#3C6146]"
          >
            Accedi
          </Button>
          <Link to="/" className="label-xs mt-5 block text-[#94A3B8] hover:text-[#4E7C59]">
            ← Torna al configuratore
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <header className="border-b border-border bg-[#1B2A20] text-[#F4F7F2]">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8">
          <span className="font-display text-base font-bold uppercase tracking-[0.2em]">
            BioEdil Abruzzo · Admin
          </span>
          <Link
            to="/"
            data-testid="admin-back-link"
            className="label-xs flex items-center gap-2 text-[#A8A29E] hover:text-[#4E7C59]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Configuratore
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] space-y-10 px-5 py-10 sm:px-8">
        <AdminMonitor token={token} />
        <AdminDataIO token={token} />

        <section data-testid="admin-ai-rules-section">
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Istruzioni assistente AI
          </h2>
          <p className="mt-1 label-xs text-muted-foreground">
            Regole tecniche inviate all'assistente insieme alla richiesta del cliente. L'assistente
            suggerisce solo lavorazioni presenti nel listino: non modifica prezzi, quantità, IVA o
            totali.
          </p>
          <div className="mt-5 space-y-3" data-testid="admin-ai-rules-list">
            {aiRules.length === 0 && (
              <p className="text-sm text-muted-foreground">Nessuna regola configurata.</p>
            )}
            {aiRules.map((rule) => (
              <div key={rule.id} className="flex flex-col gap-2 border border-border bg-card p-3 sm:flex-row">
                <Textarea
                  data-testid={`ai-rule-text-${rule.id}`}
                  rows={2}
                  defaultValue={rule.text}
                  onChange={(e) => (rule._draft = e.target.value)}
                  className="flex-1 rounded-none text-sm"
                />
                <div className="flex gap-2 sm:flex-col">
                  <Button
                    data-testid={`ai-rule-save-${rule.id}`}
                    size="sm"
                    onClick={() => saveAiRule(rule.id, rule._draft ?? rule.text)}
                    className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                  >
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    data-testid={`ai-rule-delete-${rule.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => removeAiRule(rule.id)}
                    className="rounded-none"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex flex-col gap-3 border border-border bg-[#F3F6F1] p-4 sm:flex-row sm:items-end">
            <Textarea
              data-testid="new-ai-rule-input"
              rows={2}
              placeholder="es. Se viene selezionata la demolizione del pavimento, suggerire la verifica del sottofondo."
              value={newAiRule}
              onChange={(e) => setNewAiRule(e.target.value)}
              className="flex-1 rounded-none text-sm"
            />
            <Button
              data-testid="add-ai-rule-btn"
              onClick={addAiRule}
              className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
            >
              <Plus className="mr-1 h-4 w-4" /> Aggiungi regola
            </Button>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Immagini ambienti
          </h2>
          <p className="mt-1 label-xs text-muted-foreground">
            La foto compare nella piantina quando il cliente inserisce misure valide per
            quell'ambiente. Senza foto il riquadro resta neutro.
          </p>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-zone-images">
            {zoneTypes.map((type) => {
              const url = zoneImages[type];
              const slug = type.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <div key={type} className="border border-border bg-card">
                  <label className="relative block h-32 cursor-pointer overflow-hidden bg-[#F3F6F1]">
                    {url ? (
                      <img
                        data-testid={`zone-image-preview-${slug}`}
                        src={mediaUrl(url)}
                        alt={type}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
                        <ImageIcon className="h-5 w-5" />
                        <span className="label-xs">nessuna foto</span>
                      </span>
                    )}
                    <input
                      data-testid={`zone-image-upload-${slug}`}
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onChange={(e) => uploadZonePhoto(type, e.target.files?.[0])}
                    />
                    <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-[#4E7C59] px-2 py-1 label-xs text-white">
                      <Upload className="h-3 w-3" /> carica
                    </span>
                  </label>
                  <div className="space-y-2 p-3">
                    <div className="font-display text-sm">{type}</div>
                    <Input
                      data-testid={`zone-image-url-${slug}`}
                      placeholder="oppure incolla un link https://…"
                      defaultValue={url && url.startsWith("http") ? url : ""}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== url) saveZoneUrl(type, v);
                      }}
                      className="rounded-none font-mono-t text-xs"
                    />
                    {url && (
                      <Button
                        data-testid={`zone-image-remove-${slug}`}
                        size="sm"
                        variant="outline"
                        onClick={() => removeZonePhoto(type)}
                        className="w-full rounded-none label-xs"
                      >
                        <Trash2 className="mr-1 h-3 w-3" /> Rimuovi foto
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3 border border-border bg-[#F3F6F1] p-4">
            <Input
              data-testid="new-zone-image-type"
              placeholder="Nuova tipologia (es. Mansarda)"
              value={newZoneType}
              onChange={(e) => setNewZoneType(e.target.value)}
              className="min-w-[220px] flex-1 rounded-none"
            />
            <Button
              data-testid="add-zone-image-type-btn"
              onClick={async () => {
                const t = newZoneType.trim();
                if (!t) return toast.error("Inserisci il nome della tipologia");
                try {
                  await adminSetZoneImage(token, t, "");
                  setZoneImages((z) => ({ ...z, [t]: "" }));
                  setNewZoneType("");
                  toast.success(`Tipologia "${t}" aggiunta`);
                } catch {
                  toast.error("Creazione non riuscita");
                }
              }}
              className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
            >
              <Plus className="mr-1 h-4 w-4" /> Aggiungi tipologia
            </Button>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Categorie
          </h2>
          <div className="mt-5 border border-border bg-card" data-testid="admin-categories">
            {catalog?.categories.map((cat) => (
              <div
                key={cat.key}
                className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-2.5 last:border-0"
              >
                <span className="flex flex-col">
                  <button
                    data-testid={`admin-category-up-${cat.key}`}
                    onClick={() => moveCategory(cat.key, -1)}
                    className="text-muted-foreground transition-colors hover:text-[#4E7C59]"
                    aria-label="Sposta su"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`admin-category-down-${cat.key}`}
                    onClick={() => moveCategory(cat.key, 1)}
                    className="text-muted-foreground transition-colors hover:text-[#4E7C59]"
                    aria-label="Sposta giù"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </span>
                <input
                  data-testid={`admin-category-name-${cat.key}`}
                  value={cat.name}
                  onChange={(e) =>
                    setCatalog((c) => ({
                      ...c,
                      categories: c.categories.map((x) =>
                        x.key === cat.key ? { ...x, name: e.target.value } : x
                      ),
                    }))
                  }
                  className="min-w-[180px] flex-1 border border-transparent bg-transparent px-2 py-1 text-sm outline-none hover:border-border focus:border-[#4E7C59]"
                />
                <span className="font-mono-t text-xs text-muted-foreground">
                  {catalog.items.filter((i) => i.category === cat.key).length} lavorazioni
                </span>
                <Button
                  data-testid={`admin-category-save-${cat.key}`}
                  size="sm"
                  onClick={() => saveCategory(cat)}
                  className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
                >
                  <Save className="h-3.5 w-3.5" />
                </Button>
                <Button
                  data-testid={`admin-category-delete-${cat.key}`}
                  size="sm"
                  variant="outline"
                  onClick={() => removeCategory(cat.key)}
                  className="rounded-none"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-3 border-t border-border bg-[#F3F6F1] px-4 py-3">
              <Input
                data-testid="admin-new-category-name"
                placeholder="Nuova categoria (es. Isolamento e cappotto)"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="min-w-[220px] flex-1 rounded-none"
              />
              <Button
                data-testid="admin-add-category-btn"
                onClick={addCategory}
                className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
              >
                <Plus className="mr-1 h-4 w-4" /> Aggiungi categoria
              </Button>
            </div>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Listino lavorazioni <span className="label-xs text-[#4E7C59]">prezzi netti · IVA esclusa</span>
          </h2>
          <div className="mt-5 border border-border bg-card" data-testid="admin-pricing-table">
            <div className="hidden grid-cols-[60px_1fr_120px_130px_115px_100px_115px_100px_215px] gap-3 border-b border-border px-4 py-3 label-xs text-muted-foreground sm:grid">
              <span>Foto</span>
              <span>Lavorazione</span>
              <span>Sottocategoria</span>
              <span>Link immagine</span>
              <span>Categoria</span>
              <span>Unità</span>
              <span>Superficie</span>
              <span className="text-right">Prezzo €</span>
              <span className="text-right">Attivo / Azioni</span>
            </div>
            {catalog?.items.map((item) => (
              <div
                key={item.id}
                className="grid grid-cols-2 gap-3 border-b border-border px-4 py-2.5 sm:grid-cols-[60px_1fr_120px_130px_115px_100px_115px_100px_215px] sm:items-center"
              >
                <label className="relative flex h-11 w-11 cursor-pointer items-center justify-center border border-border bg-[#F8FAFC] hover:border-[#4E7C59]">
                  {item.image_url ? (
                    <img
                      src={mediaUrl(item.image_url)}
                      alt={item.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  )}
                  <input
                    data-testid={`admin-image-upload-${item.id}`}
                    type="file"
                    accept="image/*"
                    className="absolute inset-0 cursor-pointer opacity-0"
                    onChange={(e) => uploadImage(item.id, e.target.files?.[0])}
                  />
                  <Upload className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-[#4E7C59] p-0.5 text-white" />
                </label>
                <input
                  data-testid={`admin-name-${item.id}`}
                  value={item.name}
                  onChange={(e) => editField(item.id, "name", e.target.value)}
                  className="col-span-2 border border-transparent bg-transparent px-2 py-1 text-sm outline-none hover:border-border focus:border-[#4E7C59] sm:col-span-1"
                />
                <input
                  data-testid={`admin-subcategory-${item.id}`}
                  value={item.subcategory || ""}
                  placeholder="Sottocategoria"
                  onChange={(e) => editField(item.id, "subcategory", e.target.value)}
                  className="border border-border bg-background px-2 py-1 text-xs outline-none focus:border-[#4E7C59]"
                />
                <input
                  data-testid={`admin-image-url-${item.id}`}
                  value={item.image_url || ""}
                  placeholder="https://…"
                  onChange={(e) => editField(item.id, "image_url", e.target.value)}
                  className="border border-border bg-background px-2 py-1 font-mono-t text-xs outline-none focus:border-[#4E7C59]"
                />
                <select
                  data-testid={`admin-item-category-${item.id}`}
                  value={item.category}
                  onChange={(e) => editField(item.id, "category", e.target.value)}
                  className="border border-border bg-background px-1.5 py-1 font-mono-t text-[11px] outline-none focus:border-[#4E7C59]"
                >
                  {catalog.categories.map((c) => (
                    <option key={c.key} value={c.key}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  data-testid={`admin-unit-${item.id}`}
                  value={item.unit}
                  onChange={(e) => editField(item.id, "unit", e.target.value)}
                  className="border border-border bg-background px-1.5 py-1 font-mono-t text-[11px] outline-none focus:border-[#4E7C59]"
                >
                  <option value="m2">€/m²</option>
                  <option value="ml">€/ml</option>
                  <option value="pz">€/pz</option>
                  <option value="ora">€/ora</option>
                  <option value="corpo">a corpo</option>
                  <option value="viaggio">€/viaggio</option>
                  <option value="m3">€/m³</option>
                  <option value="giorno">€/giorno</option>
                </select>
                <select
                  data-testid={`admin-basis-${item.id}`}
                  value={item.surface_basis || "none"}
                  onChange={(e) => editField(item.id, "surface_basis", e.target.value)}
                  className="border border-border bg-background px-1.5 py-1 font-mono-t text-[11px] outline-none focus:border-[#4E7C59]"
                  title="Quantità usata automaticamente"
                >
                  {BASIS_OPTIONS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
                <input
                  data-testid={`admin-price-${item.id}`}
                  type="number"
                  step="0.5"
                  value={item.price}
                  onChange={(e) => editField(item.id, "price", e.target.value)}
                  className="border border-border bg-background px-2 py-1 text-right font-mono-t text-sm outline-none focus:border-[#4E7C59]"
                />
                <div className="flex items-center justify-end gap-2">
                  <button
                    data-testid={`admin-active-${item.id}`}
                    onClick={() => toggleActive(item)}
                    title={item.active !== false ? "Attivo: visibile nel configuratore" : "Spento: nascosto ai clienti, dati conservati"}
                    className={`border px-2 py-1 label-xs transition-colors ${
                      item.active !== false
                        ? "border-[#4E7C59] bg-[#4E7C59] text-white"
                        : "border-border bg-[#F1F5F9] text-muted-foreground"
                    }`}
                  >
                    {item.active !== false ? "Attivo" : "Spento"}
                  </button>
                  <Button
                    data-testid={`admin-item-desc-toggle-${item.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => setOpenItem(openItem === item.id ? null : item.id)}
                    className="rounded-none"
                    title="Descrizione, incluso ed escluso"
                  >
                    <FileText className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    data-testid={`admin-save-price-btn-${item.id}`}
                    size="sm"
                    onClick={() => save(item)}
                    className="rounded-none bg-[#1B2A20] text-[#F4F7F2] hover:bg-[#4E7C59]"
                  >
                    <Save className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    data-testid={`admin-delete-btn-${item.id}`}
                    size="sm"
                    variant="outline"
                    onClick={() => remove(item.id)}
                    className="rounded-none"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {openItem === item.id && (
                  <div
                    data-testid={`admin-item-desc-${item.id}`}
                    className="col-span-2 grid gap-3 border-t border-border bg-[#F3F6F1] p-4 sm:col-span-9 md:grid-cols-3"
                  >
                    <div>
                      <label className="label-xs text-muted-foreground">Descrizione</label>
                      <Textarea
                        data-testid={`admin-description-${item.id}`}
                        rows={4}
                        value={item.description || ""}
                        placeholder="Cosa prevede la lavorazione…"
                        onChange={(e) => editField(item.id, "description", e.target.value)}
                        className="mt-1 rounded-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="label-xs text-muted-foreground">
                        Cosa è incluso (una voce per riga)
                      </label>
                      <Textarea
                        data-testid={`admin-included-${item.id}`}
                        rows={4}
                        value={item.included || ""}
                        placeholder={"Manodopera\nMateriali\nPulizia finale"}
                        onChange={(e) => editField(item.id, "included", e.target.value)}
                        className="mt-1 rounded-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="label-xs text-muted-foreground">
                        Cosa non è incluso (una voce per riga)
                      </label>
                      <Textarea
                        data-testid={`admin-excluded-${item.id}`}
                        rows={4}
                        value={item.excluded || ""}
                        placeholder={"Smaltimento in discarica\nOpere edili di ripristino"}
                        onChange={(e) => editField(item.id, "excluded", e.target.value)}
                        className="mt-1 rounded-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="label-xs text-muted-foreground">
                        Istruzione tecnica per l'Assistente AI (non visibile al cliente)
                      </label>
                      <Textarea
                        data-testid={`admin-ai-note-${item.id}`}
                        rows={4}
                        value={item.ai_note || ""}
                        placeholder="Quando suggerirla, quando no, cosa verificare…"
                        onChange={(e) => editField(item.id, "ai_note", e.target.value)}
                        className="mt-1 rounded-none text-sm"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="label-xs text-muted-foreground">
                        Lavorazioni correlate — una per riga: Nome | consigliata / da_verificare /
                        alternativa
                      </label>
                      <Textarea
                        data-testid={`admin-related-${item.id}`}
                        rows={4}
                        value={item.related_text ?? relatedToText(item.related)}
                        placeholder={"Autolivellante | da_verificare\nBattiscopa ceramico | consigliata"}
                        onChange={(e) => editField(item.id, "related_text", e.target.value)}
                        className="mt-1 rounded-none font-mono-t text-xs"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <Button
                        data-testid={`admin-save-desc-${item.id}`}
                        onClick={() => save(item)}
                        className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
                      >
                        <Save className="mr-2 h-4 w-4" /> Salva scheda lavorazione
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Nuova lavorazione
          </h2>
          <div className="mt-5 grid gap-3 border border-border bg-card p-5 sm:grid-cols-[1fr_150px_170px_130px_170px_120px_auto]">
            <Input
              data-testid="admin-new-name"
              placeholder="Nome lavorazione"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="rounded-none"
            />
            <Input
              data-testid="admin-new-subcategory"
              placeholder="Sottocategoria"
              value={draft.subcategory}
              onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })}
              className="rounded-none"
            />
            <select
              data-testid="admin-new-category"
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              className="border border-border bg-background px-3 py-2 text-sm outline-none"
            >
              {catalog?.categories.map((c) => (
                <option key={c.key} value={c.key}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              data-testid="admin-new-unit"
              value={draft.unit}
              onChange={(e) => setDraft({ ...draft, unit: e.target.value })}
              className="border border-border bg-background px-3 py-2 text-sm outline-none"
            >
              <option value="m2">€/m²</option>
              <option value="ml">€/ml</option>
              <option value="pz">€/pz</option>
              <option value="ora">€/ora</option>
              <option value="corpo">a corpo</option>
              <option value="viaggio">€/viaggio</option>
              <option value="m3">€/m³</option>
              <option value="giorno">€/giorno</option>
            </select>
            <select
              data-testid="admin-new-basis"
              value={draft.surface_basis}
              onChange={(e) => setDraft({ ...draft, surface_basis: e.target.value })}
              className="border border-border bg-background px-3 py-2 text-sm outline-none"
            >
              {BASIS_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
            <Input
              data-testid="admin-new-price"
              type="number"
              placeholder="Prezzo € netto"
              value={draft.price}
              onChange={(e) => setDraft({ ...draft, price: e.target.value })}
              className="rounded-none"
            />
            <Button
              data-testid="admin-add-item-btn"
              onClick={add}
              className="rounded-none bg-[#4E7C59] text-white hover:bg-[#1B2A20]"
            >
              <Plus className="mr-1 h-4 w-4" /> Aggiungi
            </Button>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-semibold uppercase tracking-tight">
            Preventivi inviati
          </h2>
          <div className="mt-5 border border-border bg-card" data-testid="admin-quotes-table">
            {quotes.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-muted-foreground">
                Nessun preventivo ancora.
              </p>
            ) : (
              quotes.map((q) => (
                <div key={q.id} className="border-b border-border last:border-0">
                  <button
                    data-testid={`admin-quote-toggle-${q.id}`}
                    onClick={() => setOpenQuote(openQuote === q.id ? null : q.id)}
                    className="flex w-full flex-wrap items-center justify-between gap-3 px-5 py-3 text-left text-sm transition-colors hover:bg-[#F1F5F9]"
                  >
                    <span className="font-medium">{q.customer_name}</span>
                    <span className="font-mono-t text-xs text-muted-foreground">{q.phone}</span>
                    <span className="font-mono-t text-xs text-muted-foreground">{q.email}</span>
                    <span className="font-mono-t text-xs text-muted-foreground">
                      {new Date(q.created_at).toLocaleString("it-IT")}
                    </span>
                    <span className="font-mono-t font-semibold text-[#4E7C59]">
                      € {fmt(q.total)}
                    </span>
                  </button>
                  <div className="flex justify-end px-5 pb-3">
                    <Button
                      data-testid={`admin-quote-delete-${q.id}`}
                      size="sm"
                      onClick={async () => {
                        if (
                          !window.confirm(
                            "Vuoi eliminare questo preventivo inviato dalla lista?\n\nIl progetto collegato, i dati cliente e la chat non verranno eliminati."
                          )
                        )
                          return;
                        try {
                          await adminDeleteQuote(token, q.id);
                          setQuotes((list) => list.filter((x) => x.id !== q.id));
                          toast.success("Preventivo eliminato dalla lista");
                        } catch {
                          toast.error("Eliminazione non riuscita");
                        }
                      }}
                      className="rounded-none bg-[#8A2E2E] text-white hover:bg-[#6B1F1F]"
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" /> Elimina
                    </Button>
                  </div>
                  {openQuote === q.id && (
                    <div
                      data-testid={`admin-quote-detail-${q.id}`}
                      className="grid gap-6 border-t border-border bg-[#F8FAFC] px-5 py-4 md:grid-cols-2"
                    >
                      <div>
                        <div className="label-xs mb-3 text-[#4E7C59]">Dati cliente</div>
                        <dl className="space-y-1.5 text-sm">
                          {[
                            ["Nome", q.first_name],
                            ["Cognome", q.last_name],
                            ["Via e città", q.address],
                            ["CAP", q.zip_code],
                            ["Provincia", q.province],
                            ["Email", q.email],
                            ["Telefono", q.phone],
                            ["Inizio lavori", q.start_period],
                            ["Note", q.notes || "—"],
                            ["Totale netto", `€ ${fmt(q.subtotal)}`],
                            ["IVA selezionata", `${q.vat_rate ?? 10}% (€ ${fmt(q.vat)})`],
                            ["Privacy", q.privacy_consent ? "Accettata" : "Non accettata"],
                            ["Email inviata", q.email_sent ? "Sì" : "No"],
                          ].map(([k, v]) => (
                            <div key={k} className="flex justify-between gap-4 border-b border-border/60 pb-1">
                              <dt className="label-xs text-muted-foreground">{k}</dt>
                              <dd className="text-right">{v || "—"}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                      <div>
                        <div className="label-xs mb-3 text-[#4E7C59]">Lavorazioni richieste</div>
                        <div className="space-y-1 text-sm">
                          {q.lines.map((l, i) => (
                            <div key={i} className="flex justify-between gap-3 border-b border-border/60 pb-1">
                              <span>
                                <span className="font-mono-t text-xs text-muted-foreground">
                                  {l.room_name} ·{" "}
                                </span>
                                {l.item_name}
                              </span>
                              <span className="whitespace-nowrap font-mono-t text-xs">
                                {l.qty} {l.unit === "m2" ? "m²" : "pz"} · € {fmt(l.total)}
                              </span>
                            </div>
                          ))}
                          <div className="flex justify-between pt-2 font-semibold">
                            <span>Totale (IVA incl.)</span>
                            <span className="font-mono-t text-[#4E7C59]">€ {fmt(q.total)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
